import type { ComponentDefinition, FormComponent, FormDocument, FormParameters, FormTemplate } from '@formbird/types';
import { createSignal, createEffect, onCleanup, onMount } from 'solid-js';
import { Subscription } from 'rxjs';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';

interface DateTimeComponentDefinition extends ComponentDefinition {
  time_24hr?: boolean;
  noCalendar?: boolean;
  allowInput?: boolean;
  displayFormat?: string;
  defaultNow?: string;
}

interface DateTimeProps {
  document: FormDocument;
  template: FormTemplate;
  componentDefinition: DateTimeComponentDefinition;
  fieldValue: any;
  fieldName: string;
  previousFieldValue: any;
  key: number;
  formParameters: FormParameters;
}

export function DateTime(props: DateTimeProps) {
  const subs = new Subscription();
  let inputRef: HTMLInputElement | undefined;
  const [fpInstance, setFpInstance] = createSignal<flatpickr.Instance | undefined>(undefined);

  const [componentDef, setComponentDef] = createSignal(props.componentDefinition);
  const [value, setValue] = createSignal(props.fieldValue);

  const changedDocumentService = (globalThis as any).FormbirdServiceInjector.get('ChangedDocumentService');

  // Initialize value from props
  createEffect(() => {
    setValue(props.fieldValue || props.document[props.fieldName]);
  });

  // Watch for document changes
  const docSub = changedDocumentService
    .watch(props.fieldName, props.document.documentId, null)
    .subscribe((newValue: any) => {
      setValue(newValue);
      const instance = fpInstance();
      if (instance && newValue) {
        instance.setDate(newValue, false);
      } else if (instance && !newValue) {
        instance.clear(false);
      }
    });
  subs.add(docSub);

  // Watch for component definition changes
  const compSub = changedDocumentService
    .watchComponentDef(props.fieldName, props.template.documentId)
    .subscribe((compDef: any) => {
      setComponentDef({ ...componentDef(), ...compDef });
    });
  subs.add(compSub);

  onCleanup(() => {
    subs.unsubscribe();
    const instance = fpInstance();
    if (instance) {
      instance.destroy();
    }
  });

  const isDisabled = () => componentDef()?.enabled === false;
  const isMandatory = () => componentDef()?.mandatory === true;

  const labelColor = () => isDisabled() ? 'text-gray-500' : isMandatory() ? 'text-red-500' : 'text-gray-900';
  const borderStyle = () => isDisabled() ? 'border-dashed' : 'border-solid';

  const inputClasses = () => `
    peer h-full w-full border-b ${borderStyle()} bg-transparent pt-4 pb-1.5
    font-sans text-sm font-normal outline outline-0 transition-all
    text-gray-900 border-gray-800
    focus:border-gray-900 focus:outline-0
    ${isDisabled() ? 'cursor-not-allowed' : ''}
  `;

  const updateValue = (newValue: Date | undefined) => {
    const isoString = newValue ? newValue.toISOString() : undefined;
    
    const changeInfo: FormComponent = {
      document: props.document,
      template: props.template,
      componentDefinition: componentDef(),
      fieldValue: isoString,
      fieldName: props.fieldName,
      formParameters: props.formParameters,
      key: props.key,
    };

    changedDocumentService.valueChanged(changeInfo, isoString);
  };

  onMount(() => {
    if (inputRef) {
      const displayFormat = componentDef()?.displayFormat || 'd/m/Y G:i K';
      const enableTime24hr = componentDef()?.time_24hr || false;
      const noCalendar = componentDef()?.noCalendar || false;
      const allowInput = componentDef()?.allowInput !== false; // Default to true if undefined

      const instance = flatpickr(inputRef, {
        enableTime: true,
        noCalendar: noCalendar,
        time_24hr: enableTime24hr,
        dateFormat: 'Z', // ISO format
        altInput: true,
        altFormat: displayFormat,
        allowInput: allowInput,
        defaultDate: value(),
        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            updateValue(selectedDates[0]);
          } else {
            updateValue(undefined);
          }
        },
        onReady: (selectedDates, dateStr, instance) => {
            // Add custom buttons
            if (instance.calendarContainer) {
                const buttonContainer = document.createElement('div');
                buttonContainer.className = 'flex justify-between p-2 border-t border-gray-200 bg-white';
                
                const clearBtn = document.createElement('button');
                clearBtn.textContent = 'Clear';
                clearBtn.className = 'text-sm text-blue-500 hover:text-blue-700 font-medium px-2 py-1 rounded';
                clearBtn.type = 'button';
                clearBtn.onclick = () => {
                    instance.clear();
                    updateValue(undefined);
                    instance.close();
                };

                const nowBtn = document.createElement('button');
                nowBtn.textContent = 'Now';
                nowBtn.className = 'text-sm text-blue-500 hover:text-blue-700 font-medium px-2 py-1 rounded';
                nowBtn.type = 'button';
                nowBtn.onclick = () => {
                    const now = new Date();
                    instance.setDate(now, true);
                    // updateValue(now); // setDate with true triggers onChange
                    instance.close();
                };

                const doneBtn = document.createElement('button');
                doneBtn.textContent = 'Done';
                doneBtn.className = 'text-sm text-blue-500 hover:text-blue-700 font-medium px-2 py-1 rounded';
                doneBtn.type = 'button';
                doneBtn.onclick = () => {
                    instance.close();
                };

                buttonContainer.appendChild(clearBtn);
                buttonContainer.appendChild(nowBtn);
                buttonContainer.appendChild(doneBtn);
                
                instance.calendarContainer.appendChild(buttonContainer);
            }
        }
      });
      setFpInstance(instance);
    }
  });

  // Update disabled state of flatpickr
  createEffect(() => {
    const instance = fpInstance();
    if (instance) {
        if (isDisabled()) {
            if (instance.altInput) instance.altInput.disabled = true;
            if (instance.mobileInput) instance.mobileInput.disabled = true;
            if (inputRef) inputRef.disabled = true;
        } else {
            if (instance.altInput) instance.altInput.disabled = false;
            if (instance.mobileInput) instance.mobileInput.disabled = false;
            if (inputRef) inputRef.disabled = false;
        }
    }
  });

  return (
    <div class="relative h-11 w-full min-w-[200px]">
      <input
        ref={inputRef}
        class={inputClasses()}
        placeholder=" "
        disabled={isDisabled()}
      />
      <label class={`pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate text-[11px] font-normal leading-tight ${labelColor()} transition-all after:absolute after:-bottom-1.5 after:block after:w-full after:scale-x-0 after:border-b-2 after:border-gray-500 after:transition-transform after:duration-300 peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.25] peer-placeholder-shown:text-blue-gray-500 peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:after:scale-x-100 peer-focus:after:border-gray-900 peer-disabled:text-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500`}>
        {componentDef()?.label}
        {isMandatory() && ' *'}
      </label>
    </div>
  );
}
